import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Blockquote, Button } from '@ui';
import { StyledInput } from '@ui/Form';
import { Category } from 'components/ClubMemberManagement/types';
import { useClubSlug } from 'components/Clubs/hooks';
import SelectComponent from 'components/SelectComponent';
import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import UserSelectionInput from './UserSelectionInput';
import { CategoryDiscountRule, CategoryDiscountTarget } from './types';

// ── Constants ─────────────────────────────────────────────────────────────────

// The type options are language-independent unit symbols (%, €), so they are
// not routed through i18n.
const TYPE_OPTIONS = [
    { value: 'percentage', label: '%' },
    { value: 'fixed', label: '€' }
];

const getTargetOptions = (t: TFunction) => [
    { value: 'all_members', label: t('common.categoryDiscounts.targetOptions.allMembers') },
    { value: 'category', label: t('common.categoryDiscounts.targetOptions.category') },
    { value: 'member', label: t('common.categoryDiscounts.targetOptions.member') }
];

// ── No-op detection ───────────────────────────────────────────────────────────

/** Stable target identity for a rule, mirroring the backend resolver. */
function ruleIdentity(rule?: Partial<CategoryDiscountRule>): string | null {
    switch (rule?.target) {
        case 'all_members':
            return 'all_members';
        case 'category':
            return rule.id != null ? `category:${rule.id}` : null;
        case 'member':
            return rule.user_email ? `member:${rule.user_email}` : null;
        default:
            return null;
    }
}

/**
 * Warn about rules that silently do nothing: a zero value (skipped by the
 * resolver) or a duplicate target (only the last rule for an identity wins).
 */
function ruleWarning(rules: Partial<CategoryDiscountRule>[], index: number, t: TFunction): string | null {
    const rule = rules[index];
    if (!rule) return null;

    if (!(Number(rule.value) > 0)) {
        return t('common.categoryDiscounts.zeroValueWarning');
    }

    const identity = ruleIdentity(rule);
    if (identity) {
        const lastIndex = rules.reduce((acc, r, i) => (ruleIdentity(r) === identity ? i : acc), -1);
        if (lastIndex !== index) {
            return t('common.categoryDiscounts.duplicateTargetWarning', { ruleNumber: lastIndex + 1 });
        }
    }

    return null;
}

// ── RHF-bound implementation (useFieldArray) ──────────────────────────────────

interface Props {
    name?: string;
    title?: string;
    fixedOnly?: boolean;
}

/**
 * useFieldArray-based editor so nested fields like `user_email`/`user_name`
 * (written by UserSelectionInput via setValue) are always part of the same RHF
 * store — no controlled/uncontrolled conflict.
 */

export default function CategoryDiscountsInput({ name = 'category_discounts', title, fixedOnly = false }: Props) {
    const { t } = useTranslation('trainingsjournal');
    const { control, getValues, setValue, watch } = useFormContext();
    const { fields, append, remove } = useFieldArray({ control, name });
    const clubSlug = useClubSlug();

    const { data: categoryData } = useSWR<{ data: Category[] }>(clubSlug ? `club-member/${clubSlug}/category` : null);
    const categories = categoryData?.data ?? [];

    const targetOptions = getTargetOptions(t);
    const categoryOptions = [
        { value: '', label: t('common.categoryDiscounts.pleaseSelect') },
        ...categories.map((c) => ({
            value: String(c.id),
            label: c.title ?? t('common.categoryDiscounts.categoryFallback', { id: c.id })
        }))
    ];

    const appendRow = () =>
        append({ target: 'all_members', type: fixedOnly ? 'fixed' : 'percentage', value: 0 } as CategoryDiscountRule);
    const setFieldValue = (path: string, value: unknown) =>
        setValue(path, value, { shouldDirty: true, shouldValidate: true });

    useEffect(() => {
        if (!fixedOnly) return;

        const rules = (getValues(name) as CategoryDiscountRule[] | undefined) ?? [];
        rules.forEach((rule, index) => {
            if (rule?.type !== 'fixed') {
                setValue(`${name}.${index}.type`, 'fixed', { shouldDirty: true, shouldValidate: true });
            }
        });
    }, [fields, fixedOnly, getValues, name, setValue]);

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <Blockquote title={title ?? t('common.categoryDiscounts.title')}>
                {t('common.categoryDiscounts.description')}
            </Blockquote>

            {fields.length === 0 && (
                <span className="tw-inline-block tw-text-center tw-text-xs tw-italic tw-text-accents-6">
                    {t('common.categoryDiscounts.noRules')}
                </span>
            )}

            {fields.map((field, index) => {
                const target = watch(`${name}.${index}.target`) as CategoryDiscountTarget;
                const categoryId = watch(`${name}.${index}.id`);
                const type = watch(`${name}.${index}.type`);
                const value = watch(`${name}.${index}.value`);
                const effectiveType = fixedOnly ? 'fixed' : type;
                const warning = ruleWarning((watch(name) as Partial<CategoryDiscountRule>[]) ?? [], index, t);

                return (
                    <div key={field.id} className="tw-flex tw-flex-col tw-gap-1">
                        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
                            <span className="tw-mb-2 tw-self-end tw-text-lg tw-text-accents-8">{index + 1}.</span>

                            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                                <span className="tw-pl-1 tw-text-sm tw-text-accents-9">
                                    {t('common.categoryDiscounts.targetLabel')}
                                </span>
                                <SelectComponent
                                    options={targetOptions}
                                    value={target}
                                    onChange={(val) => {
                                        const currentRule = getValues(`${name}.${index}`) ?? {};
                                        setFieldValue(`${name}.${index}`, {
                                            ...currentRule,
                                            target: val as CategoryDiscountTarget,
                                            id: undefined,
                                            user_email: undefined,
                                            user_name: undefined
                                        });
                                    }}
                                />
                            </div>

                            {target === 'category' && (
                                <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                                    <span className="tw-pl-1 tw-text-sm tw-text-accents-9">
                                        {t('common.categoryDiscounts.categoryLabel')}
                                    </span>
                                    <SelectComponent
                                        options={categoryOptions}
                                        value={categoryId !== undefined ? String(categoryId) : ''}
                                        onChange={(val) =>
                                            setFieldValue(`${name}.${index}.id`, val ? Number(val) : undefined)
                                        }
                                    />
                                </div>
                            )}

                            {target === 'member' && (
                                <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-self-start">
                                    <UserSelectionInput prefix={`${name}.${index}`} clubSlug={clubSlug} />
                                </div>
                            )}

                            {!fixedOnly && (
                                <div className="tw-flex-col tw-gap-1">
                                    <span className="tw-pl-1 tw-text-sm tw-text-accents-9">
                                        {t('common.categoryDiscounts.typeLabel')}
                                    </span>
                                    <SelectComponent
                                        options={TYPE_OPTIONS}
                                        value={type}
                                        onChange={(val) => setFieldValue(`${name}.${index}.type`, val)}
                                    />
                                </div>
                            )}

                            <StyledInput
                                label={t('common.categoryDiscounts.discountLabel')}
                                type="number"
                                min={0}
                                step={0.01}
                                labelRight={effectiveType === 'percentage' ? '%' : '€'}
                                containerClassName="tw-w-[120px]"
                                inputClassName="tw-min-w-0"
                                className="!tw-my-0"
                                value={value ?? 0}
                                onChange={(e) => setFieldValue(`${name}.${index}.value`, Number(e.currentTarget.value))}
                            />

                            <div className="tw-mb-1 tw-self-end">
                                <Button
                                    size="sm"
                                    color="error"
                                    type="button"
                                    aria-label={t('common.categoryDiscounts.removeAriaLabel', { number: index + 1 })}
                                    onPress={() => remove(index)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </div>
                        </div>
                        {warning && <span className="tw-pl-6 tw-text-xs tw-text-warning">{warning}</span>}
                    </div>
                );
            })}

            <Button size="xs" color="secondary" type="button" onClick={appendRow}>
                {t('common.categoryDiscounts.addRule')}
            </Button>
        </div>
    );
}

// ── Controlled editor (form builder — no RHF field array) ─────────────────────

interface EditorProps {
    value: CategoryDiscountRule[];
    onChange: (next: CategoryDiscountRule[]) => void;
    title?: string;
    fixedOnly?: boolean;
}

/**
 * RHF-independent controlled editor used by the imperative form builder
 * (FieldTypeOptions, QuestionBlock). Member target uses a plain email input
 * since UserSelectionInput depends on field-array RHF state.
 */
export function CategoryDiscountsEditor({ value, onChange, title, fixedOnly = false }: EditorProps) {
    const { t } = useTranslation('trainingsjournal');
    const clubSlug = useClubSlug();
    const rules = value ?? [];

    const { data: categoryData } = useSWR<{ data: Category[] }>(clubSlug ? `club-member/${clubSlug}/category` : null);
    const categories = categoryData?.data ?? [];

    const targetOptions = getTargetOptions(t);
    const categoryOptions = [
        { value: '', label: t('common.categoryDiscounts.pleaseSelect') },
        ...categories.map((c) => ({
            value: String(c.id),
            label: c.title ?? t('common.categoryDiscounts.categoryFallback', { id: c.id })
        }))
    ];

    const update = (index: number, patch: Partial<CategoryDiscountRule>) =>
        onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
    const removeAt = (index: number) => onChange(rules.filter((_, i) => i !== index));
    const append = () =>
        onChange([...rules, { target: 'all_members', type: fixedOnly ? 'fixed' : 'percentage', value: 0 }]);

    useEffect(() => {
        if (!fixedOnly || !rules.some((rule) => rule?.type !== 'fixed')) return;

        onChange(rules.map((rule) => ({ ...rule, type: 'fixed' })));
    }, [fixedOnly, onChange, rules]);

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <Blockquote title={title ?? t('common.categoryDiscounts.title')}>
                {t('common.categoryDiscounts.description')}
            </Blockquote>

            {rules.length === 0 && (
                <span className="tw-inline-block tw-text-center tw-text-xs tw-italic tw-text-accents-6">
                    {t('common.categoryDiscounts.noRules')}
                </span>
            )}

            {rules.map((rule, index) => {
                const effectiveType = fixedOnly ? 'fixed' : rule.type;
                const warning = ruleWarning(rules, index, t);

                return (
                    <div key={index} className="tw-flex tw-flex-col tw-gap-1">
                        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
                            <span className="tw-mb-2 tw-self-end tw-text-lg tw-text-accents-8">{index + 1}.</span>

                            <div className="tw-my-1 tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                                <span className="tw-pl-1 tw-text-sm tw-text-accents-9">
                                    {t('common.categoryDiscounts.targetLabel')}
                                </span>
                                <SelectComponent
                                    options={targetOptions}
                                    value={rule.target}
                                    onChange={(val) =>
                                        update(index, {
                                            target: val as CategoryDiscountTarget,
                                            id: undefined,
                                            user_email: undefined,
                                            user_name: undefined
                                        })
                                    }
                                />
                            </div>

                            {rule.target === 'category' && (
                                <div className="tw-my-1 tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                                    <span className="tw-pl-1 tw-text-sm tw-text-accents-9">
                                        {t('common.categoryDiscounts.categoryLabel')}
                                    </span>
                                    <SelectComponent
                                        options={categoryOptions}
                                        value={rule.id !== undefined ? String(rule.id) : ''}
                                        onChange={(val) => update(index, { id: val ? Number(val) : undefined })}
                                    />
                                </div>
                            )}

                            {rule.target === 'member' && (
                                <StyledInput
                                    label={t('common.categoryDiscounts.memberEmailLabel')}
                                    type="email"
                                    bordered
                                    containerClassName="tw-flex-1"
                                    value={rule.user_email ?? ''}
                                    onChange={(e) => update(index, { user_email: e.currentTarget.value })}
                                />
                            )}

                            {!fixedOnly && (
                                <div className="tw-flex tw-flex-col tw-gap-1">
                                    <span className="tw-pl-1 tw-text-sm tw-text-accents-9">
                                        {t('common.categoryDiscounts.typeLabel')}
                                    </span>
                                    <SelectComponent
                                        options={TYPE_OPTIONS}
                                        value={rule.type}
                                        onChange={(val) => update(index, { type: val as CategoryDiscountRule['type'] })}
                                    />
                                </div>
                            )}

                            <StyledInput
                                label={t('common.categoryDiscounts.discountLabel')}
                                type="number"
                                className="!tw-my-1"
                                min={0}
                                step={0.01}
                                labelRight={effectiveType === 'percentage' ? '%' : '€'}
                                bordered
                                containerClassName="tw-w-[120px]"
                                inputClassName="tw-min-w-0"
                                value={rule.value}
                                onChange={(e) => update(index, { value: Number(e.currentTarget.value) })}
                            />

                            <div className="tw-mb-1 tw-self-end">
                                <Button
                                    size="sm"
                                    color="error"
                                    type="button"
                                    aria-label={t('common.categoryDiscounts.removeAriaLabel', { number: index + 1 })}
                                    onPress={() => removeAt(index)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </div>
                        </div>
                        {warning && <span className="tw-pl-6 tw-text-xs tw-text-warning">{warning}</span>}
                    </div>
                );
            })}

            <Button size="xs" color="secondary" type="button" onClick={append}>
                {t('common.categoryDiscounts.addRule')}
            </Button>
        </div>
    );
}
